/**
 * Fait : décrit les documents des collections `marqueursDeSuppression` et
 *   `versionsDeMigration` — le registre de ce qui doit disparaître (le cadrage
 *   exige des données supprimables) et la trace de ce que les migrations ont
 *   appliqué à la base.
 *
 * Ne fait pas : il ne purge rien et ne migre rien. La purge est un geste
 *   d'exploitation que porte `apps/api/src/comptes/export-et-suppression.ts` ;
 *   appliquer et défaire les migrations est le métier du moteur de migration.
 */

import { z } from 'zod';

import { identifiant, nomDonne } from '../schemas.ts';
import { NOMS_DES_COLLECTIONS } from './collections.ts';

/**
 * La demande de suppression d'un document, en deux temps : marquer d'abord,
 * purger ensuite. Le marqueur survit à la purge — c'est lui qui prouve qu'une
 * donnée demandée disparue a réellement disparu.
 */
export const marqueurDeSuppression = z
  .object({
    /** La collection visée — forcément l'une de celles du produit. */
    collectionCible: z.enum(NOMS_DES_COLLECTIONS),

    documentId: identifiant,
    demandeParId: identifiant,
    demandeLe: z.date(),

    /** Posée quand la purge a réellement eu lieu — absente avant. */
    purgeLe: z.date().optional(),
  })
  .strict();

export type MarqueurDeSuppression = z.infer<typeof marqueurDeSuppression>;

/**
 * Une migration appliquée. L'aller-retour-aller du critère 5 s'appuie sur
 * cette trace : appliquer écrit une ligne, défaire la retire, et l'écart
 * entre la trace et l'état réel de la base doit toujours être nul.
 */
export const versionDeMigration = z
  .object({
    /** Numéro de version, strictement positif, unique — l'ordre des migrations. */
    version: z.number().int().min(1),

    nom: nomDonne,
    appliqueeLe: z.date(),
  })
  .strict();

export type VersionDeMigration = z.infer<typeof versionDeMigration>;

/**
 * UN VERROU. Il ne porte aucune donnée du produit : son seul rôle est d'être
 * un document que plusieurs transactions touchent, pour que la base puisse
 * les arbitrer entre elles.
 *
 * MongoDB n'arbitre que les écritures sur un MÊME document. Deux fermetures
 * d'accès visant deux comptes différents ne se voyaient donc pas, et
 * pouvaient laisser la famille sans aucun administrateur — mesuré cinq fois
 * sur cinq le 2026-08-07. Décision de Charles le même jour : « quand il y a
 * un risque de concurrence, on met toujours un verrou ».
 *
 * `_id` est le NOM du verrou, pas un identifiant hexadécimal : il n'y en a
 * qu'une poignée, et on les désigne par ce qu'ils protègent.
 */
export const verrou = z
  .object({
    _id: nomDonne,

    /** Quand il a été pris pour la dernière fois. Pour le regard, pas pour la règle. */
    prisLe: z.date(),
  })
  .strict();

export type Verrou = z.infer<typeof verrou>;
